import {
    Box, Button, Center, Checkbox, Flex, FormControl, FormLabel, Grid, GridItem, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Spinner, Text, Textarea, Tooltip, Radio, RadioGroup, Stack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CustomToast, Dropdown, FormInput } from "../../../../components";
import { useForm } from "react-hook-form";
import { baseUrl, useFetch } from "../../../../hooks/useFetch";
import CustomTable, { Column } from "../../../../components/customTable/CustomTable";
import { showDateTime } from "../../../../utilities/helper";
import { ViewIcon, DownloadIcon } from "@chakra-ui/icons";
import { useAuth } from "../../../../hooks/useAuth";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AttachmentIcon } from "@chakra-ui/icons";
import { refreshHistory } from "../../../../components/History/modal";
import { Controller } from "react-hook-form";
import RichTextEditor from "../../../../components/RichEditorText/richEditorText";
interface ISendMessageForm {
    subject: string;
    description: string;
}
interface IAttachment {
    id: number;
    fileName: string;
    relativePath: string;
}
interface IMessageHistory {
    id: number;
    subject: string;
    type: string;
    to: string;
    mailBody: string;
    attachments: IAttachment[];
    extraEmails: string[] | string | null;
    sentBy: string;
    createdAt: string;
}
interface Props {
    pocName: string;
    pocEmail: string;
    leadId: number;
    viewType?: "table" | "card";
     onMessageSaved?: () => void;
}
const schema = yup.object({
    subject: yup.string().trim().required("Subject is required"),
    description: yup.string().trim().required("Mail Body is required").test("not-empty", "Mail Body cannot be empty", (value) => !!value?.trim()),
});

const SendMessageForm = ({ pocName, pocEmail, leadId, viewType = "table",   onMessageSaved,}: Props) => {
    const { addToast } = CustomToast();
    const { fetchApi, loading } = useFetch(addToast);
    const [selectedMailBody, setSelectedMailBody] = useState("");
    const [showBodyModal, setShowBodyModal] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [attachments, setAttachments] = useState<File[]>([]);
    const [messages, setMessages] = useState<IMessageHistory[]>([]);
    const { loginObj } = useAuth();
    const [showAttachmentModal, setShowAttachmentModal] = useState(false);
    const [selectedAttachments, setSelectedAttachments] =
        useState<IAttachment[]>([]);

    const [emailInput, setEmailInput] = useState("");

    const [emailList, setEmailList] = useState<string[]>(pocEmail ? [pocEmail.toLowerCase()] : []);

    const [emailError, setEmailError] = useState("");
    const [ccEmailInput, setCcEmailInput] = useState("");
    const [ccEmailList, setCcEmailList] = useState<string[]>([]);
    const [ccEmailError, setCcEmailError] = useState("");
    const [includeSignature, setIncludeSignature] = useState(true);
    const [signatureType, setSignatureType] = useState<"Short" | "Full">("Full");
    const [includeDisclaimer, setIncludeDisclaimer] = useState(true);

    const [userSignature, setUserSignature] = useState<{ shortSignature: string; fullSignature: string } | null>(null);
    const [websiteDisclaimer, setWebsiteDisclaimer] = useState<string>("");
    const [loadingSignature, setLoadingSignature] = useState(false);
    const [loadingDisclaimer, setLoadingDisclaimer] = useState(false);

    useEffect(() => {
        const fetchSignature = async () => {
            if (includeSignature && !userSignature && !loadingSignature) {
                setLoadingSignature(true);
                try {
                    const res = await fetchApi<{ shortSignature: string; fullSignature: string }>("UserSignature", "GET");
                    if (res) {
                        setUserSignature({
                            shortSignature: res.shortSignature || "",
                            fullSignature: res.fullSignature || "",
                        });
                    }
                } catch (error) {
                    console.error("Error fetching signature:", error);
                } finally {
                    setLoadingSignature(false);
                }
            }
        };
        fetchSignature();
    }, [includeSignature, userSignature, loadingSignature]);

    useEffect(() => {
        const fetchDisclaimer = async () => {
            if (includeDisclaimer && !websiteDisclaimer && !loadingDisclaimer) {
                setLoadingDisclaimer(true);
                try {
                    const res = await fetchApi<{ disclaimer: string }>("WebsiteSetting", "GET");
                    if (res) {
                        setWebsiteDisclaimer(res.disclaimer || "");
                    }
                } catch (error) {
                    console.error("Error fetching disclaimer:", error);
                } finally {
                    setLoadingDisclaimer(false);
                }
            }
        };
        fetchDisclaimer();
    }, [includeDisclaimer, websiteDisclaimer, loadingDisclaimer]);

    useEffect(() => {
        if (pocEmail) {
            setEmailList([pocEmail.toLowerCase()]);
        } else {
            setEmailList([]);
        }
    }, [pocEmail]);

    const isValidEmail = (email: string) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

    const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
            if (!trimmed) return;
            if (!isValidEmail(trimmed)) { setEmailError("Invalid email address."); return; }
            if (emailList.includes(trimmed)) { setEmailError("Email already added."); return; }
            setEmailList(prev => [...prev, trimmed]);
            setEmailInput("");
            setEmailError("");
        }
    };

    const handleEmailBlur = () => {
        const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
        if (!trimmed) return;
        if (!isValidEmail(trimmed)) { setEmailError("Invalid email address."); return; }
        if (emailList.includes(trimmed)) { setEmailError("Email already added."); return; }
        setEmailList(prev => [...prev, trimmed]);
        setEmailInput("");
        setEmailError("");
    };

    const removeEmail = (email: string) =>
        setEmailList(prev => prev.filter(e => e !== email));


    const { control, handleSubmit, reset, register, formState: { errors },
    } = useForm<ISendMessageForm>({
        resolver: yupResolver(schema),
        defaultValues: {
            subject: "", description: "",
        },
    });

    const loadMessages = async () => {
        const res = await fetchApi<IMessageHistory[]>(`LeadRequirement/GetMessageHistory?leadId=${leadId}`, "GET");
        if (Array.isArray(res)) {
            setMessages(res);
        } else {
            setMessages([]);
        }
    };
    useEffect(() => {
        if (leadId) {
            loadMessages();
        }
    }, [leadId]);


    //handle cc email input and list
    const handleCcEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();

            const trimmed = ccEmailInput.trim().replace(/,$/, "").toLowerCase();
            if (!trimmed) return;

            if (!isValidEmail(trimmed)) {
                setCcEmailError("Invalid email address.");
                return;
            }

            if (ccEmailList.includes(trimmed)) {
                setCcEmailError("Email already added.");
                return;
            }

            setCcEmailList(prev => [...prev, trimmed]);
            setCcEmailInput("");
            setCcEmailError("");
        }
    };

    const handleCcEmailBlur = () => {
        const trimmed = ccEmailInput.trim().replace(/,$/, "").toLowerCase();
        if (!trimmed) return;

        if (!isValidEmail(trimmed)) {
            setCcEmailError("Invalid email address.");
            return;
        }

        if (ccEmailList.includes(trimmed)) {
            setCcEmailError("Email already added.");
            return;
        }

        setCcEmailList(prev => [...prev, trimmed]);
        setCcEmailInput("");
        setCcEmailError("");
    };

    const removeCcEmail = (email: string) =>
        setCcEmailList(prev => prev.filter(e => e !== email));

    const videoExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm", ".wmv"];
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

    const MAX_IMAGE_DOC_SIZE = 5 * 1024 * 1024;
    const MAX_VIDEO_SIZE = 20 * 1024 * 1024;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const files = Array.from(e.target.files);
        const validFiles: File[] = [];
        const errors: string[] = [];

        files.forEach((file) => {
            const ext = "." + file.name.split(".").pop()?.toLowerCase();
            const isVideo = videoExtensions.includes(ext);
            const isImage = imageExtensions.includes(ext);

            const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_DOC_SIZE;

            if (file.size > maxSize) {
                const limitText = isVideo ? "20 MB" : "5 MB";
                const typeLabel = isVideo ? "Video" : isImage ? "Image" : "File";
                errors.push(`${typeLabel} '${file.name}' exceeds ${limitText} limit.`);
            } else {
                validFiles.push(file);
            }
        });

        if (errors.length > 0) {
            addToast({ message: errors.join(" "), status: "error" });
        }

        setAttachments((prev) => [...prev, ...validFiles]);
    };

    const onSubmit = async (data: ISendMessageForm) => {

        let finalEmails = [...emailList];
        const trimmed = emailInput.trim().replace(/,$/, "").toLowerCase();
        if (trimmed) {
            if (isValidEmail(trimmed)) {
                if (!finalEmails.includes(trimmed)) {
                    finalEmails.push(trimmed);
                    setEmailList(prev => prev.includes(trimmed) ? prev : [...prev, trimmed]);
                    setEmailInput("");
                    setEmailError("");
                }
            } else {
                setEmailError("Invalid email address.");
                return;
            }
        }

        if (finalEmails.length === 0) {
            setEmailError("Please add at least one recipient email.");
            return;
        }

        //for cc emails
        let finalCcEmails = [...ccEmailList];

        const trimmedCc = ccEmailInput.trim().replace(/,$/, "").toLowerCase();

        if (trimmedCc) {
            if (isValidEmail(trimmedCc)) {
                if (!finalCcEmails.includes(trimmedCc)) {
                    finalCcEmails.push(trimmedCc);
                }
            } else {
                setCcEmailError("Invalid email address.");
                return;
            }
        }
        const formData = new FormData();
        formData.append("SenderName", pocName);
        formData.append("SenderEmail", pocEmail);
        formData.append("Subject", data.subject);
        formData.append("Title", data.subject);
        formData.append("Body", data.description);
        formData.append("EmailTo", pocEmail);
        formData.append("IncludeSignature", includeSignature.toString());
        formData.append("IncludeDisclaimer", includeDisclaimer.toString());
        formData.append("SignatureType", signatureType);
        formData.append("LeadId", leadId.toString());
        finalEmails.forEach((email) => {
            formData.append("ExtraEmails", email);
        });
        finalCcEmails.forEach(email => {
            formData.append("CCExtraEmails", email);
        });
        attachments.forEach((file) => {
            formData.append("Attachments", file);
        });
        const res = await fetchApi("LeadRequirement/send", "POST", formData, null, "Email Sent Successfully", true);
        if (res) {
            reset();
            setAttachments([]);
            setEmailList(pocEmail ? [pocEmail.toLowerCase()] : []);
            setShowForm(false);
            setIncludeSignature(false);
            setIncludeDisclaimer(false);
            setSignatureType("Short");
            loadMessages();
              onMessageSaved?.();
            refreshHistory("Lead", leadId);
        }
    };

    const stripHtml = (html: string) => {
        const doc = new DOMParser().parseFromString(html || "", "text/html");
        return doc.body.textContent || "";
    };


    const downloadAttachment = async (attachmentId: number, fileName: string) => {
        const response = await fetch(
            `${baseUrl}LeadRequirement/download-attachment/${attachmentId}`,
            { headers: { Authorization: `Bearer ${loginObj?.token}`, }, });
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    };


    const closeModal = () => {
        setShowForm(false);
        setEmailList([]);
        setEmailInput("");
        setEmailError("");
        setIncludeSignature(false);
        setIncludeDisclaimer(false);
        setSignatureType("Short");
    };

    return (
        <Box>
            <Flex justify="space-between" mb={4}>
                <Text className="font-poppins text_lg text_semibold">
                    Send Email
                </Text>
                <Button size="sm" bg="#4048D6" color="white" onClick={() => { setEmailList(pocEmail ? [pocEmail.toLowerCase()] : []); setShowForm(true); }} >
                    Send Email
                </Button>
            </Flex>
            {viewType === "table" ? (

                <CustomTable value={messages} pagination={false} showEmptyMessage >
                     <Column header="S.No" body={(_, index) => index + 1} />
                    <Column header="Subject" field="subject" />
                    <Column header="Type" field="type" />
                    <Column header="Mail Body" body={(row: IMessageHistory) => (
                        <Tooltip label="View Message" placement="top" hasArrow maxW="500px" whiteSpace="pre-wrap">
                            <ViewIcon cursor="pointer" color="#4048D6" onClick={() => {
                                setSelectedMailBody(row.mailBody);
                                setShowBodyModal(true);
                            }}
                            />
                        </Tooltip>
                    )}
                    />

                    <Column header="Attachments" body={(row: IMessageHistory) =>
                        row.attachments?.length > 0 ? (
                            <Tooltip label="Show & Download Attachments" hasArrow >
                                <AttachmentIcon cursor="pointer" color="#4048D6" boxSize={5}
                                    onClick={() => {
                                        setSelectedAttachments(row.attachments);
                                        setShowAttachmentModal(true);
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Text>-</Text>
                        )
                    }
                    />
                    <Column
                        header="Sent To"
                        body={(row: any) => {
                            const toEmails: string[] =
                                Array.isArray(row.extraEmails)
                                    ? row.extraEmails
                                    : typeof row.extraEmails === "string" && row.extraEmails
                                        ? row.extraEmails.split(",").map((e: string) => e.trim())
                                        : [];

                            const ccEmails: string[] =
                                Array.isArray(row.ccExtraEmails)
                                    ? row.ccExtraEmails
                                    : typeof row.ccExtraEmails === "string" && row.ccExtraEmails
                                        ? row.ccExtraEmails.split(",").map((e: string) => e.trim())
                                        : [];

                            const allEmails = [...toEmails, ...ccEmails];

                            if (allEmails.length === 0) return <Text>-</Text>;

                            return (
                                <Tooltip
                                    label={
                                        <Flex direction="column" gap={2} p={1}>
                                            {toEmails.length > 0 && (
                                                <>
                                                    <Text fontWeight="bold" fontSize="sm">
                                                        To
                                                    </Text>
                                                    {toEmails.map((email: string, index: number) => (
                                                        <Text key={`to-${index}`} fontSize="sm">
                                                            {email}
                                                        </Text>
                                                    ))}
                                                </>
                                            )}

                                            {ccEmails.length > 0 && (
                                                <>
                                                    <Text fontWeight="bold" fontSize="sm" mt={1}>
                                                        CC
                                                    </Text>
                                                    {ccEmails.map((email: string, index: number) => (
                                                        <Text key={`cc-${index}`} fontSize="sm">
                                                            {email}
                                                        </Text>
                                                    ))}
                                                </>
                                            )}
                                        </Flex>
                                    }
                                    placement="top"
                                    hasArrow
                                    bg="white"
                                    color="black"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                >
                                    <Text fontSize="sm" noOfLines={1} maxW="180px" cursor="pointer">
                                        {allEmails.join(", ")}
                                    </Text>
                                </Tooltip>
                            );
                        }}
                    />
                    <Column header="Sent At" body={(row: IMessageHistory) => showDateTime(row.createdAt)} />
                    <Column header="Sent By" field="sentBy" />
                </CustomTable>
            ) : (
                <>
                    {loading ? (
                        <Flex justify="center" align="center" height="300px" >
                            <Spinner size="lg" color="#4048D6" />
                        </Flex>
                    ) : messages.length === 0 ? (
                        <Flex direction="column" justify="center" align="center" height="300px" >
                            <Text color="gray.500" fontWeight="500">
                                No Messages Found
                            </Text>
                        </Flex>
                    ) : (
                        <Grid templateColumns={{ base: "1fr", lg: "repeat(1, 1fr)", }} gap={4}>
                            {messages.map((message) => (

                                <Box key={message.id} border="1px solid #E4E7EC" borderRadius="12px" p={4} bg="white">
                                    <Flex justify="space-between" mb={3}>
                                        <Text fontWeight="600"> {message.subject}</Text>

                                    </Flex>
                                    <Grid templateColumns="repeat(5, auto)" gap={6} alignItems="center" mb={3}>
                                        <Box>
                                            <Text color="gray.500" fontSize="xs"> Type</Text>
                                            <Text fontSize="sm"> {message.type}</Text>
                                        </Box>

                                        <Box>
                                            <Text color="gray.500" fontSize="xs"> Sent By</Text>
                                            <Text fontSize="sm"> {message.sentBy}</Text>
                                        </Box>

                                        <Box>
                                            <Text color="gray.500" fontSize="xs">Sent At</Text>
                                            <Text fontSize="sm">{showDateTime(message.createdAt)}</Text>
                                        </Box>

                                        <Box>
                                            <Text color="gray.500" fontSize="xs"> Files</Text>
                                            {message.attachments?.length > 0 ? (
                                                <AttachmentIcon cursor="pointer" color="#4048D6" boxSize={4}
                                                    onClick={() => { setSelectedAttachments(message.attachments); setShowAttachmentModal(true); }}
                                                />
                                            ) : (
                                                <Text fontSize="sm">-</Text>
                                            )}
                                        </Box>

                                        <Box textAlign="right">
                                            <Text color="gray.500" fontSize="xs">Mail Body</Text>
                                            <Tooltip label="View Message">
                                                <ViewIcon cursor="pointer" color="#4048D6" boxSize={5}
                                                    onClick={() => { setSelectedMailBody(message.mailBody); setShowBodyModal(true); }}
                                                />
                                            </Tooltip>
                                        </Box>
                                    </Grid>
                                </Box>
                            ))}
                        </Grid>
                    )}
                </>
            )}


            <Modal isOpen={showForm} onClose={closeModal} size="4xl">
                <ModalOverlay />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalContent maxH="700px" overflow="scroll">
                        <ModalHeader>Send Email</ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                            <Flex direction="column" gap={4}>

                                <GridItem colSpan={2}>
                                    <FormControl isInvalid={!!emailError}>
                                        <FormLabel className="font-poppins text_medium text_md">  To </FormLabel>
                                        {emailList.length > 0 && (
                                            <Flex flexWrap="wrap" gap={2} mb={2} p={2} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
                                                {emailList.map(email => (
                                                    <Flex key={email} align="center" gap={1} bg="#EEF0FD" color="#4048D6" borderRadius="full" px={3} py={1} fontSize="sm" fontWeight="500">
                                                        <Text>{email}</Text>
                                                        <Text cursor="pointer" fontWeight="bold" ml={1} onClick={() => removeEmail(email)} _hover={{ color: "red.500" }}>×</Text>
                                                    </Flex>
                                                ))}
                                            </Flex>
                                        )}

                                        <Input placeholder="Type email and press Enter or , to add" value={emailInput}
                                            onChange={e => { setEmailInput(e.target.value); setEmailError(""); }} onKeyDown={handleEmailKeyDown} onBlur={handleEmailBlur} bg="white"
                                        />
                                        {emailError && <Text color="red.500" fontSize="sm" mt={1}>{emailError}</Text>}
                                        <Text color="gray.400" fontSize="xs" mt={1}>
                                            Press <strong>Enter</strong> or <strong>,</strong> to add multiple emails
                                        </Text>
                                    </FormControl>
                                </GridItem>
                                <GridItem colSpan={2}>
                                    <FormControl isInvalid={!!ccEmailError}>
                                        <FormLabel className="font-poppins text_medium text_md">
                                            CC
                                        </FormLabel>

                                        {ccEmailList.length > 0 && (
                                            <Flex flexWrap="wrap" gap={2} mb={2} p={2} border="1px solid" borderColor="gray.200" borderRadius="md" bg="gray.50">
                                                {ccEmailList.map(email => (
                                                    <Flex key={email} align="center" gap={1} bg="#EEF0FD" color="#4048D6" borderRadius="full" px={3} py={1} fontSize="sm" fontWeight="500">
                                                        <Text>{email}</Text>
                                                        <Text cursor="pointer" fontWeight="bold" ml={1} onClick={() => removeCcEmail(email)} _hover={{ color: "red.500" }}>
                                                            ×
                                                        </Text>
                                                    </Flex>
                                                ))}
                                            </Flex>
                                        )}

                                        <Input
                                            placeholder="Type email and press Enter or , to add"
                                            value={ccEmailInput}
                                            onChange={(e) => {
                                                setCcEmailInput(e.target.value);
                                                setCcEmailError("");
                                            }}
                                            onKeyDown={handleCcEmailKeyDown}
                                            onBlur={handleCcEmailBlur}
                                        />

                                        {ccEmailError && (
                                            <Text color="red.500" fontSize="sm" mt={1}>
                                                {ccEmailError}
                                            </Text>
                                        )}

                                        <Text color="gray.400" fontSize="xs" mt={1}>
                                            Press <strong>Enter</strong> or <strong>,</strong> to add multiple emails
                                        </Text>
                                    </FormControl>
                                </GridItem>
                                <FormInput name="subject" control={control} label="Subject" isRequired errors={errors} />
                                <FormControl isInvalid={!!errors.description}>
                                    <FormLabel>
                                        Mail Body <Text as="span" color="red.500">*</Text>
                                    </FormLabel>

                                    <Controller
                                        control={control}
                                        name="description"
                                        render={({ field }) => (
                                            <RichTextEditor
                                                value={field.value ?? "Hello"}
                                                onChange={field.onChange}
                                            />
                                        )}
                                    />


                                    {errors.description && (
                                        <Text color="red.500">
                                            {errors.description.message}
                                        </Text>
                                    )}
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Attachments</FormLabel>
                                    <Input id="attachment-upload" type="file" multiple accept=".pdf,.png,.jpg,.jpeg" display="none" onChange={handleFileChange} />
                                    <Button as="label" htmlFor="attachment-upload" variant="outline" width="fit-content" borderRadius="10px" colorScheme="blue" cursor="pointer" >
                                        Upload Files
                                    </Button>
                                    {attachments.length > 0 && (
                                        <Grid mt={3} templateColumns="repeat(3, 1fr)" gap={2} maxH="150px" overflowY="auto">
                                            {attachments.map((file, index) => (
                                                <GridItem key={index}>
                                                    <Flex direction="column" gap={2} p={2} border="1px solid #E2E8F0" borderRadius="8px" className="font-poppins text_xsm text_regular" >
                                                        <Text noOfLines={2} wordBreak="break-word" >
                                                            {file.name}
                                                        </Text>

                                                        <Button size="xs" colorScheme="red" variant="ghost" onClick={() =>
                                                            setAttachments((prev) => prev.filter((_, i) => i !== index))}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Flex>
                                                </GridItem>
                                            ))}
                                        </Grid>
                                    )}
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Email Options</FormLabel>

                                    <Flex direction="column" gap={4}>
                                        <Box>
                                            <Checkbox isChecked={includeSignature}
                                                onChange={(e) => setIncludeSignature(e.target.checked)}
                                            >
                                                Show Signature
                                            </Checkbox>
                                            {includeSignature && (
                                                <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                                    <RadioGroup onChange={(val) => setSignatureType(val as "Short" | "Full")} value={signatureType}>
                                                        <Flex gap={4} mb={2}>
                                                            <Radio value="Short" colorScheme="blue">Short Signature</Radio>
                                                            <Radio value="Full" colorScheme="blue">Full Signature</Radio>
                                                        </Flex>
                                                    </RadioGroup>
                                                    {loadingSignature ? (
                                                        <Flex align="center" gap={2} mt={2}>
                                                            <Spinner size="xs" color="#4048D6" />
                                                            <Text fontSize="xs" color="gray.500">Loading signature...</Text>
                                                        </Flex>
                                                    ) : (
                                                        <Box 
                                                            mt={2} 
                                                            p={3} 
                                                            bg="gray.50" 
                                                            borderRadius="md" 
                                                            border="1px solid" 
                                                            borderColor="gray.200" 
                                                            maxH="150px" 
                                                            overflowY="auto"
                                                            fontSize="sm"
                                                        >
                                                            {signatureType === "Short" ? (
                                                                userSignature?.shortSignature ? (
                                                                    <Box dangerouslySetInnerHTML={{ __html: userSignature.shortSignature }} />
                                                                ) : (
                                                                    <Text color="gray.400" fontStyle="italic">No short signature configured.</Text>
                                                                )
                                                            ) : (
                                                                userSignature?.fullSignature ? (
                                                                    <Box dangerouslySetInnerHTML={{ __html: userSignature.fullSignature }} />
                                                                ) : (
                                                                    <Text color="gray.400" fontStyle="italic">No full signature configured.</Text>
                                                                )
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>

                                        <Box>
                                            <Checkbox
                                                isChecked={includeDisclaimer}
                                                onChange={(e) => setIncludeDisclaimer(e.target.checked)}
                                            >
                                                Disclaimer
                                            </Checkbox>
                                            {includeDisclaimer && (
                                                <Box mt={3} pl={6} borderLeft="2px solid" borderColor="blue.100">
                                                    {loadingDisclaimer ? (
                                                        <Flex align="center" gap={2}>
                                                            <Spinner size="xs" color="#4048D6" />
                                                            <Text fontSize="xs" color="gray.500">Loading disclaimer...</Text>
                                                        </Flex>
                                                    ) : (
                                                        <Box 
                                                            p={3} 
                                                            bg="gray.50" 
                                                            borderRadius="md" 
                                                            border="1px solid" 
                                                            borderColor="gray.200" 
                                                            maxH="150px" 
                                                            overflowY="auto"
                                                            fontSize="sm"
                                                        >
                                                            {websiteDisclaimer ? (
                                                                <Box dangerouslySetInnerHTML={{ __html: websiteDisclaimer }} />
                                                            ) : (
                                                                <Text color="gray.400" fontStyle="italic">No disclaimer configured.</Text>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Flex>
                                </FormControl>
                            </Flex>
                        </ModalBody>

                        <ModalFooter gap={3}>
                            <Button variant="outline" onClick={() => setShowForm(false)}>  Cancel </Button>
                            <Button bg="#4F46E5" _hover={{ bg: "#4338CA" }} color="white" type="submit" isLoading={loading}  > Send </Button>
                        </ModalFooter>
                    </ModalContent>
                </form>
            </Modal>



            <Modal isOpen={showBodyModal} onClose={() => setShowBodyModal(false)} size="4xl" >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Mail Body </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Box dangerouslySetInnerHTML={{ __html: selectedMailBody, }} />
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={() => setShowBodyModal(false)}> Close </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>



            <Modal isOpen={showAttachmentModal} onClose={() => setShowAttachmentModal(false)} size="lg" >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Attachments</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Flex direction="column" gap={3}>
                            {selectedAttachments.length > 0 ? (
                                selectedAttachments.map((file) => (
                                    <Flex key={file.id} align="center" justify="space-between" p={3} border="1px solid #E2E8F0" borderRadius="8px"
                                    >
                                        <Flex align="center" gap={2} >
                                            <AttachmentIcon color="#4048D6" />
                                            <Text noOfLines={1} fontSize="sm" > {file.fileName} </Text>
                                        </Flex>
                                        <Tooltip label="Download Attachment" hasArrow   >
                                            <DownloadIcon cursor="pointer" color="#4048D6" boxSize={5}
                                                onClick={() =>
                                                    downloadAttachment(file.id, file.fileName)
                                                }
                                            />
                                        </Tooltip>
                                    </Flex>
                                ))
                            ) : (
                                <Text> No attachments found</Text>
                            )}
                        </Flex>
                    </ModalBody>

                    <ModalFooter>
                        <Button onClick={() => setShowAttachmentModal(false)} > Close </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default SendMessageForm;